<template>
  <v-container fluid class="rpd-page">
    <header class="query-page-header">
      <div class="query-page-copy">
        <div class="query-page-eyebrow">Relative percent difference</div>
        <h1>Compare a sample with its duplicate</h1>
        <p>
          Pair a primary sample with its field duplicate, confirm the acceptance criteria, then
          generate a workbook. Every calculation is written to the workbook rather than shown here.
        </p>
      </div>
      <div class="query-page-actions">
        <v-btn class="my-2" @click="drawer = true">
          <v-icon left color="info">folder_open</v-icon>
          Load saved query
        </v-btn>
        <v-btn class="my-2" @click="openSaveDialog">
          <v-icon left color="info">save</v-icon>
          Save query
        </v-btn>
      </div>
    </header>

    <section class="query-workspace">
      <v-layout>
        <RpdSavedQueriesDrawer
          :drawer="drawer"
          :queries="savedQueries"
          :loading="savedQueriesLoading"
          :unavailable="savedQueriesUnavailable"
          @update:drawer="drawer = $event"
          @load:query="loadQuery"
          @delete:query="deleteQuery"
        />

        <v-flex class="pt-0" style="min-width: 0;">
          <div class="query-summary">
            <div class="query-summary-copy">
              <strong>{{ savedQueryName || 'New query' }}</strong>
              <span>{{ statusText }}</span>
            </div>
            <span class="field-note">
              Accepting RPD &gt; {{ thresholds.acceptanceThreshold }}% fails a row.
            </span>
          </div>

          <v-progress-linear v-if="resolving || generating" class="my-0" :height="4" color="info" indeterminate />

          <v-card class="rpd-card">
            <v-tabs v-model="tab" class="rpd-tabs" slider-color="info">
              <v-tab href="#rpd-pairing">
                <v-icon left>call_split</v-icon>
                Sample pairing
              </v-tab>
              <v-tab href="#rpd-criteria">
                <v-icon left>tune</v-icon>
                Criteria &amp; exclusions
              </v-tab>
            </v-tabs>

            <v-tabs-items v-model="tab">
              <v-tab-item value="rpd-pairing">
                <div class="pairing">
                  <div class="section-head"><h2>Sample pairing</h2></div>
                  <p class="section-lede">
                    Each side runs its own filter set. Fill in the primary sample's filters on the
                    left and the duplicate's on the right.
                  </p>

                  <!-- The first of the two statements of the verdict; the live one is at the
                       foot, beside Generate. Both come from the same band model. -->
                  <RpdVerdictBand :band="band" />

                  <div class="pairing__sides">
                    <RpdPairingFilters
                      side="primary"
                      :filter="pairing.Primary"
                      :locations="locations"
                      :aliases="primaryAliases"
                      :media-types="mediaTypes"
                      @update:filter="setSide('Primary', $event)"
                    />
                    <RpdPairingFilters
                      side="duplicate"
                      :filter="pairing.Duplicate"
                      :locations="locations"
                      :aliases="duplicateAliases"
                      :media-types="mediaTypes"
                      :collision-note="identity.collides ? 'Both sides point at this location.' : ''"
                      @update:filter="setSide('Duplicate', $event)"
                    />
                  </div>

                  <!--
                    The samples are fetched on demand rather than on every change, so the action is
                    explicit and carries its own readout of whether the verdict is current.
                  -->
                  <div class="pairing__actions">
                    <span class="field-note">{{ confirmNote }}</span>
                    <span class="spacer" />
                    <v-btn
                      color="info"
                      :disabled="resolving"
                      :loading="resolving"
                      @click="confirmFilters"
                    >
                      <v-icon left>search</v-icon>
                      Confirm filter
                    </v-btn>
                  </div>
                </div>
              </v-tab-item>

              <v-tab-item value="rpd-criteria">
                <RpdCriteriaExclusions
                  :thresholds="thresholds"
                  :excluded-analyte-ids="excludedAnalyteIds"
                  :catalogue="analyteCatalogue"
                  :loading="catalogueLoading"
                  @update:thresholds="thresholds = $event"
                  @update:excludedAnalyteIds="excludedAnalyteIds = $event"
                />
              </v-tab-item>
            </v-tabs-items>
          </v-card>

          <RpdResolvedSamples :resolution="resolution" :identity="identity" :band="band" />

          <!--
            The submitted notice. Generation is asynchronous and the page does not track the job, so
            this is the only feedback the reviewer gets here: the work is in the message centre, which
            is the same contract the discrete-data-query report uses.
          -->
          <v-alert v-model="submitted" type="info" dismissible class="mb-4">
            Report generation request is submitted. Please check the status of the process at the
            <a class="field-note__link" @click="goToMessageCenter">message center</a>.
          </v-alert>

          <!--
            The export bar. The bar has one state because the page no longer polls: the button
            submits, and the notice above carries the outcome.
          -->
          <div class="card-actions card-actions--foot">
            <span class="export-notes">
              <span class="field-note card-actions__note">{{ exportNote }}</span>
            </span>
            <span class="spacer" />
            <v-btn
              color="info"
              :disabled="!canGenerate"
              :loading="generating"
              @click="onExport"
            >
              <v-icon left>file_download</v-icon>
              Generate workbook
            </v-btn>
          </div>
        </v-flex>
      </v-layout>
    </section>

    <RpdSaveQueryDialog
      v-model="saveDialog"
      :saved-queries="savedQueries"
      :current-user-id="currentUserId"
      :initial-name="savedQueryName"
      :initial-shared="savedQueryShared"
      :initial-description="savedQueryDescription"
      :busy="saving"
      @save="onSave"
    />
  </v-container>
</template>

<script>
  import RpdPairingFilters from './RpdPairingFilters'
  import RpdResolvedSamples from './RpdResolvedSamples'
  import RpdCriteriaExclusions from './RpdCriteriaExclusions'
  import RpdVerdictBand from './RpdVerdictBand'
  import RpdSavedQueriesDrawer from './RpdSavedQueriesDrawer'
  import RpdSaveQueryDialog from './RpdSaveQueryDialog'

  import {
    RPD_EXPORT_NOTE,
    RPD_GATE_IDLE,
    RPD_GATE_STALE,
    emptyPairing,
    emptyThresholds,
    gateBand,
    identityComparison,
    isGateOpen,
  } from '@/helpers/rpd'

  /*
   * The RPD Report page.
   *
   * The pairing gate is the interface: the two filter sets are resolved server side when the reviewer
   * confirms them, and the page reports the verdict rather than deciding it. Generation is unblocked
   * only by an explicit ready, so a gate this build does not recognise blocks rather than opens, and a
   * verdict is never shown as current for filters that have been edited since it was read.
   *
   * Generation is asynchronous - Generate answers 202 and the workbook is produced out of band - so
   * the page does not track the job. It reports the submission and points the reviewer at the
   * message centre, which is where progress and the finished workbook live. That is the same
   * contract the discrete-data-query report uses, and it keeps one place of truth for the message.
   */
  export default {
    name: 'RpdReport',

    components: {
      RpdPairingFilters,
      RpdResolvedSamples,
      RpdCriteriaExclusions,
      RpdVerdictBand,
      RpdSavedQueriesDrawer,
      RpdSaveQueryDialog,
    },

    data() {
      return {
        tab: 'rpd-pairing',
        drawer: false,
        saveDialog: false,
        saving: false,

        pairing: emptyPairing(),
        thresholds: emptyThresholds(),
        excludedAnalyteIds: [],

        /*
         * The filters are confirmed, not watched: samples are fetched by the Confirm filter button,
         * so editing a filter marks the verdict stale instead of re-querying on every change.
         */
        filtersConfirmed: false,
        filtersChanged: false,

        /* The submitted notice. Shown once the request is accepted, dismissed by the reviewer. */
        submitted: false,

        savedQueryId: null,
        savedQueryName: '',
        savedQueryDescription: '',
        savedQueryShared: false,
      }
    },

    computed: {
      aliasOptions() {
        return this.$store.getters['rpd/aliasOptions']
      },

      locationAliasOptions() {
        return this.$store.getters['rpd/locationAliasOptions']
      },

      aliases() {
        return this.aliasOptions.aliases
      },

      /*
       * The aliases each side's picker offers. A side that names a location is offered that
       * location's own aliases, because an alias id is a sample type and only means anything
       * against the location it was read from; a side that names none keeps the tenant-wide
       * vocabulary, which is the "any location" state. While a location's list is still in flight
       * the side is offered nothing, so a stale list is never shown as if it were the current one.
       */
      primaryAliases() {
        return this.aliasesForSide('Primary')
      },

      duplicateAliases() {
        return this.aliasesForSide('Duplicate')
      },

      mediaTypes() {
        return this.aliasOptions.mediaTypes
      },

      aliasLabels() {
        return this.aliasOptions.aliasLabels
      },

      tenantOptions() {
        return this.$store.getters['rpd/tenantOptions']
      },

      analyteCatalogue() {
        return this.$store.getters['rpd/analyteCatalogue']
      },

      catalogueLoading() {
        return this.tab === 'rpd-criteria' && !this.analyteCatalogue.length
      },

      gateKind() {
        return this.$store.getters['rpd/gateKind']
      },

      resolution() {
        return this.$store.getters['rpd/resolution']
      },

      savedQueries() {
        return this.$store.getters['rpd/savedQueries']
      },

      savedQueriesUnavailable() {
        return this.$store.getters['rpd/savedQueriesUnavailable']
      },

      resolving() {
        return this.$store.state.rpd.resolving
      },

      generating() {
        return this.$store.state.rpd.generating
      },

      savedQueriesLoading() {
        return !this.$store.state.rpd.savedQueriesLoaded && !this.savedQueriesUnavailable
      },

      currentUserId() {
        const user = this.$store.getters['authenticate/currentUser']
        return (user && user.userId) || ''
      },

      /*
       * The location pickers offer the tenant's discrete stations, read from the existing
       * datasource module rather than a route of RPD's own. An empty list leaves both selects as
       * "any location", which is the documented no-filter state.
       */
      locations() {
        return this.$store.getters['rpd/locationOptions']
      },

      band() {
        if (!this.filtersConfirmed) {
          return this.filtersChanged ? RPD_GATE_STALE : RPD_GATE_IDLE
        }
        return gateBand(this.gateKind)
      },

      identity() {
        return identityComparison(
          this.pairing.Primary,
          this.pairing.Duplicate,
          {},
          this.aliasLabels,
        )
      },

      canGenerate() {
        return this.filtersConfirmed
          && isGateOpen(this.gateKind)
          && !this.generating
      },

      /* The confirm row's readout, so the reviewer can see whether the verdict is current. */
      confirmNote() {
        if (!this.filtersConfirmed) return 'Filters are not confirmed yet.'
        if (this.filtersChanged) return 'Filters changed since they were confirmed.'
        return 'Filters confirmed. Samples fetched.'
      },

      exportNote() {
        return RPD_EXPORT_NOTE
      },

      statusText() {
        if (!this.filtersConfirmed) return 'Confirm the filter to fetch samples'
        if (this.gateKind === 'ready') return 'Ready to generate'
        return 'Set both sides of the pairing'
      },
    },

    watch: {
      /*
       * Editing a filter does not query: it marks the verdict stale, so the reviewer sees that what
       * is on screen describes the previous filters until Confirm filter fetches the new ones.
       */
      pairing: {
        deep: true,
        handler() {
          this.filtersChanged = true
        },
      },

      tab(value) {
        /* The catalogue is only needed by the picker, so it loads when the tab is first opened. */
        if (value === 'rpd-criteria') this.loadCatalogue()
      },

      'pairing.Primary.LocationId'(locationId) {
        this.onLocationChange('Primary', locationId)
      },

      'pairing.Duplicate.LocationId'(locationId) {
        this.onLocationChange('Duplicate', locationId)
      },
    },

    async mounted() {
      await this.loadVocabularies()
      await this.loadSavedQueries()
    },

    methods: {
      async loadVocabularies() {
        try {
          await this.$store.dispatch('rpd/loadAliasOptions')
          await this.$store.dispatch('rpd/loadLocationOptions')
          const options = await this.$store.dispatch('rpd/loadTenantOptions')

          /* The tenant's stored thresholds are the starting point; the UI owns the per-query layer. */
          this.thresholds = {
            acceptanceThreshold: options.acceptanceThreshold,
            detectionLimitMultiplier: options.detectionLimitMultiplier,
          }
          this.excludedAnalyteIds = [...options.excludedAnalyteIds]
        } catch (error) {
          this.fail('The RPD filters could not be loaded.', error)
        }
      },

      loadCatalogue() {
        if (this.analyteCatalogue.length) return
        this.$store.dispatch('rpd/loadAnalyteCatalogue').catch((error) => {
          this.fail('The analyte list could not be loaded.', error)
        })
      },

      async loadSavedQueries() {
        try {
          await this.$store.dispatch('rpd/loadSavedQueries')
        } catch (error) {
          this.fail('Saved queries could not be loaded.', error)
        }
      },

      setSide(side, filter) {
        this.pairing = { ...this.pairing, [side]: filter }
      },

      /* The alias list a side is offered, per the rule stated beside `primaryAliases`. */
      aliasesForSide(side) {
        const locationId = this.pairing[side].LocationId
        if (locationId === null || locationId === undefined || locationId === '') {
          return this.aliases
        }

        const loaded = this.locationAliasOptions[side]
        if (!loaded || loaded.locationId !== locationId) return []
        return loaded.options ? loaded.options.aliases : []
      },

      /*
       * A location change re-scopes that side's aliases. The selected ids came from the vocabulary
       * of the location that was named when they were picked, and the translation treats an id it
       * does not know as the sample type it names - so a leftover id would not fail loudly, it
       * would quietly narrow the query to nothing. Anything the newly named location does not
       * offer is therefore dropped.
       *
       * This also runs when a saved query is opened, where the ids and the location arrive
       * together; nothing is dropped there because the stored ids are the location's own.
       */
      async onLocationChange(side, locationId) {
        let options = null

        try {
          options = await this.$store.dispatch('rpd/loadLocationAliasOptions', { side, locationId })
        } catch (error) {
          this.fail('The aliases for the chosen location could not be loaded.', error)
          return
        }

        /* A slower earlier request must not prune against the list it was answered with. */
        if (this.pairing[side].LocationId !== locationId) return

        const offered = options ? options.aliases : this.aliases
        const filter = this.pairing[side]
        const selected = filter.AliasLabels || []
        const kept = selected.filter((id) => offered.some((alias) => alias.id === id))

        if (kept.length !== selected.length) {
          this.setSide(side, { ...filter, AliasLabels: kept })
        }
      },

      /*
       * Fetches the samples the two filter sets select. The verdict is only accepted as current when
       * the server answered, so a failed resolve leaves the page on the idle band and Generate shut.
       */
      async confirmFilters() {
        if (await this.resolveNow()) {
          this.filtersConfirmed = true
          this.filtersChanged = false
        }
      },

      async resolveNow() {
        try {
          await this.$store.dispatch('rpd/resolvePairing', this.pairing)
          return true
        } catch (error) {
          this.fail('The pairing could not be resolved.', error)
          return false
        }
      },

      /*
       * The request is submitted, not tracked. Once the API has accepted it the workbook is produced
       * out of band, so the page says where to watch it and stops - the message centre owns progress,
       * failure and the download from that point on.
       */
      async onExport() {
        try {
          await this.$store.dispatch('rpd/generate', {
            pairing: this.pairing,
            thresholds: this.thresholds,
            excludedAnalyteIds: this.excludedAnalyteIds,
          })
          this.submitted = true
        } catch (error) {
          this.fail('The workbook could not be submitted.', error)
        }
      },

      goToMessageCenter() {
        this.$router.push({ name: 'MessageCenter' })
      },

      openSaveDialog() {
        this.saveDialog = true
      },

      async onSave(payload) {
        this.saving = true

        try {
          const shared = {
            name: payload.name,
            isShared: payload.isShared,
            description: payload.description,
            pairing: this.pairing,
            thresholds: this.thresholds,
            excludedAnalyteIds: this.excludedAnalyteIds,
            includeMedia: true,
          }

          /*
           * A rename moves the document id, so the id is only reused when the name is unchanged.
           * An overwrite of a query someone else owns is a create by name: the store keys on the
           * name, so the same call replaces it for everyone.
           */
          const canUpdateInPlace = !!this.savedQueryId &&
            payload.state.key === 'update' &&
            payload.name === this.savedQueryName

          const saved = canUpdateInPlace
            ? await this.$store.dispatch('rpd/updateSavedQuery', { id: this.savedQueryId, ...shared })
            : await this.$store.dispatch('rpd/createSavedQuery', shared)

          this.savedQueryId = saved.Id
          this.savedQueryName = saved.Name
          this.savedQueryDescription = saved.Description
          this.savedQueryShared = saved.IsShared
          this.saveDialog = false

          await this.loadSavedQueries()
          this.notify('Saved.', 'success')
        } catch (error) {
          /*
           * A 409 means the name was taken between the list load and the write. Re-reading the
           * list re-renders the dialog's banner from the server's own answer rather than guessing.
           */
          if (error && error.response && error.response.status === 409) {
            await this.loadSavedQueries()
          }
          this.fail('The query could not be saved.', error)
        } finally {
          this.saving = false
        }
      },

      async loadQuery(id) {
        try {
          const loaded = await this.$store.dispatch('rpd/loadSavedQuery', id)

          this.pairing = loaded.pairing
          this.thresholds = loaded.thresholds
          this.excludedAnalyteIds = loaded.excludedAnalyteIds

          this.savedQueryId = loaded.query.Id
          this.savedQueryName = loaded.query.Name
          this.savedQueryDescription = loaded.query.Description
          this.savedQueryShared = loaded.query.IsShared

          this.drawer = false
          this.tab = 'rpd-pairing'

          /* Loading a definition replaces the pairing wholesale, so it is confirmed and re-resolved. */
          await this.confirmFilters()
        } catch (error) {
          this.fail('The saved query could not be opened.', error)
        }
      },

      async deleteQuery(query) {
        try {
          await this.$store.dispatch('rpd/deleteSavedQuery', query.Id)

          if (this.savedQueryId === query.Id) {
            this.savedQueryId = null
            this.savedQueryName = ''
            this.savedQueryDescription = ''
            this.savedQueryShared = false
          }
          this.notify('Deleted.', 'success')
        } catch (error) {
          this.fail('The query could not be deleted.', error)
        }
      },

      notify(text, color) {
        this.$store.dispatch('app/setSnackbar', { text, color, icon: 'check_circle' })
      },

      fail(summary, error) {
        const detail = error && error.response && error.response.data && error.response.data.Message
        this.$store.dispatch('app/setSnackbar', {
          text: detail ? `${summary} ${detail}` : summary,
          color: 'error',
          icon: 'error',
        })
      },
    },
  }
</script>
